import { createFileRoute } from "@tanstack/react-router";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { LegalPageLayout, LegalSectionHeading } from "@/components/legal-page-layout";
import { buildLocalizedSeoLinks, getLocalizedSeoUrls, ogImageUrl } from "@/lib/seo";

export const Route = createFileRoute("/terms")({
	component: Terms,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath: "/terms",
		});

		return {
			meta: [
				{
					title:
						"Terms of Service - AgentFramer | MCP-Native Media Generation for AI Agents",
				},
				{
					name: "description",
					content:
						"Read the AgentFramer Terms of Service. These terms govern your use of our platform for AI agent-driven image, video, and audio generation through MCP.",
				},
				{
					property: "og:title",
					content:
						"Terms of Service - AgentFramer | MCP-Native Media Generation for AI Agents",
				},
				{
					property: "og:description",
					content:
						"Terms governing your use of AgentFramer's MCP-native media generation platform for AI agents.",
				},
				{
					property: "og:image",
					content: ogImageUrl("terms"),
				},
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("terms") },
			],
			links: buildLocalizedSeoLinks({
				matches,
				internalPath: "/terms",
			}),
		};
	},
});

const sidebarSections = [
	{ id: "terms-acceptance", number: "01", label: "Acceptance" },
	{ id: "terms-usage", number: "02", label: "Usage Policy" },
	{ id: "terms-api", number: "03", label: "API Limits" },
	{ id: "terms-ip", number: "04", label: "Intellectual Property" },
	{ id: "terms-liability", number: "05", label: "Liability" },
	{ id: "terms-termination", number: "06", label: "Termination" },
];

const apiTiers = [
	{ tier: "Free_Tier", limit: "1,000 req/day", note: "no SLA" },
	{ tier: "Developer_Pro", limit: "50,000 req/day", note: "99.9% SLA" },
	{ tier: "Enterprise_Monolith", limit: "Unlimited*", note: "custom SLA" },
];

function Terms() {
	return (
		<div style={{ background: "var(--km-surface)", color: "var(--km-on-surface)", minHeight: "100vh" }}>
			<PublicHeader />
			<LegalPageLayout
				contentsLabel="Terms Sections"
				description="These terms govern your access to and use of AgentFramer's MCP-native media generation platform. By using the service, you agree to be bound by these terms."
				lastUpdated="2026.03.01.UTC"
				sections={sidebarSections}
				title={
					<>
						TERMS OF
						<br />
						<span style={{ color: "#5A33D4" }}>SERVICE.</span>
					</>
				}
				version="Terms v2.4.0"
			>
						<section id="terms-acceptance" className="space-y-5 scroll-mt-28">
							<LegalSectionHeading number="01" title="Acceptance of Terms" />
							<p className="text-sm leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
								By accessing or using AgentFramer ("Service"), you agree to be bound by
								these Terms of Service ("Terms"). If you do not agree to these Terms, you
								may not access or use the Service. We reserve the right to update these
								Terms at any time, and your continued use of the Service constitutes
								acceptance of any changes.
							</p>
						</section>

						<section id="terms-usage" className="space-y-5 scroll-mt-28">
							<LegalSectionHeading number="02" title="Usage Policy" />
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
								<div
									className="p-5 space-y-3"
									style={{
										background: "var(--km-surface-low)",
										border: "1px solid rgba(255,255,255,0.08)",
									}}
								>
									<div className="flex items-center gap-2">
										<span
											aria-hidden="true"
											className="material-symbols-outlined"
											style={{ fontSize: "18px", color: "#2b9a66" }}
										>
											task_alt
										</span>
										<span
											className="text-sm font-bold"
											style={{ color: "var(--km-on-surface)" }}
										>
											Authorized Use
										</span>
									</div>
									<ul className="space-y-2">
										{[
											"01. AI agent-driven generation via MCP",
											"02. Lawful commercial and personal projects",
											"03. Integration into your own products",
										].map((item) => (
											<li
												key={item}
												className="text-xs"
												style={{
													fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
													color: "var(--km-on-surface-muted)",
												}}
											>
												{item}
											</li>
										))}
									</ul>
								</div>
								<div
									className="p-5 space-y-3"
									style={{
										background: "var(--km-surface-low)",
										border: "1px solid rgba(255,255,255,0.08)",
									}}
								>
									<div className="flex items-center gap-2">
										<span
											aria-hidden="true"
											className="material-symbols-outlined"
											style={{ fontSize: "18px", color: "#f87171" }}
										>
											block
										</span>
										<span
											className="text-sm font-bold"
											style={{ color: "var(--km-on-surface)" }}
										>
											Prohibited Actions
										</span>
									</div>
									<ul className="space-y-1.5">
										{[
											"Generating illegal or harmful content",
											"Reverse engineering the MCP bridge",
											"Sharing account credentials",
											"Circumventing rate limits",
											"Impersonating other users or entities",
										].map((item) => (
											<li
												key={item}
												className="text-xs flex items-start gap-2"
												style={{ color: "var(--km-on-surface-muted)" }}
											>
												<span style={{ color: "#f87171" }}>×</span>
												{item}
											</li>
										))}
									</ul>
								</div>
							</div>
						</section>

						<section id="terms-api" className="space-y-5 scroll-mt-28">
							<LegalSectionHeading number="03" title="API Rate Limits" />
							<p className="text-sm leading-relaxed mb-4" style={{ color: "var(--km-on-surface-muted)" }}>
								Rate limits are enforced per workspace. Exceeding your tier's limits will
								result in 429 responses until the window resets. Enterprise customers may
								negotiate custom burst allowances.
							</p>
							<div
								className="overflow-hidden"
								style={{
									border: "1px solid rgba(255,255,255,0.08)",
									fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
								}}
							>
								<div
									className="grid grid-cols-3 text-xs px-4 py-2.5"
									style={{
										background: "var(--km-surface-low)",
										borderBottom: "1px solid rgba(255,255,255,0.08)",
										color: "var(--km-on-surface-muted)",
									}}
								>
									<span>TIER_IDENTIFIER</span>
									<span>REQUEST_LIMIT</span>
									<span>SLA_CLASS</span>
								</div>
								{apiTiers.map((row, idx) => (
									<div
										key={row.tier}
										className="grid grid-cols-3 text-xs px-4 py-3"
										style={{
											background: idx % 2 === 0 ? "var(--km-surface)" : "var(--km-surface-low)",
											borderBottom:
												idx < apiTiers.length - 1
													? "1px solid rgba(255,255,255,0.08)"
													: "none",
										}}
									>
										<span style={{ color: "#6B3FF2" }}>{row.tier}</span>
										<span style={{ color: "var(--km-on-surface)" }}>{row.limit}</span>
										<span style={{ color: "var(--km-on-surface-muted)" }}>{row.note}</span>
									</div>
									))}
								</div>
						</section>

						<section id="terms-ip" className="space-y-5 scroll-mt-28">
							<LegalSectionHeading number="04" title="Intellectual Property" />
							<p className="text-sm leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
								AgentFramer and its original content, features, and functionality are owned
								by AgentFramer and are protected by international copyright, trademark,
								patent, trade secret, and other intellectual property laws. You may not
								copy, modify, distribute, sell, or lease any part of our Service without
								permission.
							</p>
							<p className="text-sm leading-relaxed mt-3" style={{ color: "var(--km-on-surface-muted)" }}>
								You retain ownership of content you create or upload to AgentFramer. By
								posting content, you grant us a non-exclusive, worldwide license to
								display, store, and deliver your content as necessary to operate the
								Service.
							</p>
							<div
								className="mt-4 px-4 py-3 text-xs leading-relaxed"
								style={{
									background: "rgba(234,40,4,0.04)",
									border: "1px solid #6B3FF2",
									color: "#6B3FF2",
									fontFamily: "inherit",
								}}
							>
								Generated assets produced via AgentFramer's MCP bridge are owned by you,
								subject to the output policies of the underlying model providers.
							</div>
						</section>

						<section id="terms-liability" className="space-y-5 scroll-mt-28">
							<LegalSectionHeading number="05" title="Limitation of Liability" />
							<div
								className="p-4 text-xs leading-relaxed uppercase tracking-wide"
								style={{
									background: "var(--km-surface-low)",
									border: "1px solid rgba(255,255,255,0.08)",
									color: "var(--km-on-surface-muted)",
									fontFamily: "inherit",
									letterSpacing: "0.04em",
									lineHeight: "1.8",
								}}
							>
								THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF
								ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY LAW, AGENTFRAMER SHALL NOT BE
								LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
								DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR
								INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE
								LOSSES.
							</div>
						</section>

						<section id="terms-termination" className="space-y-5 scroll-mt-28">
							<LegalSectionHeading number="06" title="Termination" />
							<p className="text-sm leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
								We may terminate or suspend your access to the Service immediately, without
								prior notice or liability, for any reason, including breach of these Terms.
								Upon termination, your right to use the Service will immediately cease. You
								may also terminate your account at any time through your account settings.
							</p>
						</section>
			</LegalPageLayout>

			{/* Footer */}
			<PublicFooter />
		</div>
	);
}
